import "./ManageFeatures.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, DatePicker, Space, Tooltip, Typography } from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import {
  AppstoreOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import { useAppTheme } from "../../context/ThemeProvider";
import { DEFAULT_DATES_RANGE_OPTIONS, PAGE_LIMIT } from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import FeaturesAPI from "../api/FeaturesAPI";
import ManageCustomAppsModal from "./ManageCustomAppsModal/ManageCustomAppsModal";
import ManageFeaturesModal from "./ManageFeaturesModal/ManageFeaturesModal";
import {
  INITIAL_FILTERS,
  MANAGEABLE_FEATURES,
  UNFILTERABLE_FEATURES,
} from "./manageFeaturesUtils";
import UsersTable from "./UsersTable";

const { RangePicker } = DatePicker;

const initialState = {
  loading: false,
  list: [],
  page: 1,
  limit: PAGE_LIMIT,
  total: 0,
};

const ManageFeatures = ({ sensitiveAllowedActions }) => {
  const { COLORS } = useAppTheme();
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCustomAppsModalOpen, setIsCustomAppsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const initialLoadRef = useRef(false);

  const onChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // // Reset pagination when filters change
    // setState((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    onChangeFilters("startDate", dates?.[0] || null);
    onChangeFilters("endDate", dates?.[1] || null);
  };

  const fetchUsers = useCallback(
    async (currentPage, currentLimit, currentFilters) => {
      setState((prev) => ({ ...prev, loading: true }));

      try {
        // Prepare filters for API
        const apiFilters = {
          search: currentFilters.search,
          page: currentPage,
          limit: currentLimit,
          ...(currentFilters.userTier && { userTier: currentFilters.userTier }),
          ...(currentFilters.planType && { planType: currentFilters.planType }),
          ...(currentFilters.startDate && {
            createdAtFrom: currentFilters.startDate
              .startOf("day")
              .toISOString(),
          }),
          ...(currentFilters.endDate && {
            createdAtTo: currentFilters.endDate.endOf("day").toISOString(),
          }),

          // Dynamically include all feature flags that are set to "enabled".
          // Adding a new feature to MANAGEABLE_FEATURES is all that's needed.
          // UNFILTERABLE_FEATURES are skipped: STORAGE_LIMIT has its own range
          // params below, and the backend ignores ?MANGO_AI=true.
          ...Object.values(MANAGEABLE_FEATURES).reduce((acc, feature) => {
            if (
              currentFilters[feature] === "enabled" &&
              !UNFILTERABLE_FEATURES.includes(feature)
            ) {
              acc[feature] = "true";
            }
            return acc;
          }, {}),

          // STORAGE_LIMIT_FROM and STORAGE_LIMIT_TO are special cases that are not part of MANAGEABLE_FEATURES.
          ...(currentFilters.STORAGE_LIMIT_FROM && {
            STORAGE_LIMIT_FROM: String(currentFilters.STORAGE_LIMIT_FROM),
          }),
          ...(currentFilters.STORAGE_LIMIT_TO && {
            STORAGE_LIMIT_TO: String(currentFilters.STORAGE_LIMIT_TO),
          }),
        };

        const resp = await FeaturesAPI.getAllCreatorsWithFeatureFlag(
          apiFilters
        );

        if (resp?.code === 0) {
          // Handle both possible response structures
          const responseData = resp.result?.data || resp.result || [];
          const total = resp.result?.total || responseData.length || 0;

          setState((prev) => ({
            ...prev,
            list: responseData.map((item) => ({
              ...item,
              key: item._id,
            })),
            total,
            loading: false,
          }));
        } else {
          showAppError(resp);
          setState((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        showAppError(error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    []
  );

  const reloadUsers = () => {
    setState((prev) => ({ ...prev, list: [], page: 1 }));
    setSelectedRowKeys([]);
    setSelectedUsers([]);
    fetchUsers(1, state.limit, filters);
  };

  const handleTableChange = (pagination, tableFilters) => {
    const newPage = pagination.current;
    const newLimit = pagination.pageSize;

    // Check if filters have actually changed
    let filtersChanged = false;
    const newFilters = {};

    if (tableFilters) {
      // Convert single-selection filters from arrays to single values
      if (tableFilters.userTier && Array.isArray(tableFilters.userTier)) {
        newFilters.userTier = tableFilters.userTier[0] || null;
      } else {
        newFilters.userTier = tableFilters.userTier || null;
      }

      if (tableFilters.planType && Array.isArray(tableFilters.planType)) {
        newFilters.planType = tableFilters.planType[0] || null;
      } else {
        newFilters.planType = tableFilters.planType || null;
      }

      // Check if filters actually changed
      if (
        filters.userTier !== newFilters.userTier ||
        filters.planType !== newFilters.planType
      ) {
        filtersChanged = true;
      }
    }

    if (filtersChanged) {
      // Filters changed - update filters and reset to page 1
      setFilters((prev) => ({
        ...prev,
        userTier: newFilters.userTier,
        planType: newFilters.planType,
      }));

      setState((prev) => ({
        ...prev,
        page: 1, // Reset to first page when filters change
        limit: newLimit,
      }));
    } else {
      // Only pagination/page size change - keep current filters
      setState((prev) => ({
        ...prev,
        page: newPage,
        limit: newLimit,
      }));
    }
  };

  const handleSelectChange = (selectedKeys, selectedRows) => {
    setSelectedRowKeys(selectedKeys);
    setSelectedUsers(selectedRows);
  };

  const handleUpdateFeatures = () => {
    if (selectedUsers.length > 0) {
      // Open modal with preloaded users
      setIsModalOpen(true);
    } else {
      // Open modal with CSV upload
      setIsModalOpen(true);
    }
  };

  const handleManageCustomApps = () => {
    setIsCustomAppsModalOpen(true);
  };

  const disabledDate = (current) => {
    // Disable future dates
    return current && current > moment().endOf("day");
  };

  // Ref to track filter changes
  const filtersRef = useRef(JSON.stringify(filters));

  // Single useEffect to handle all data fetching
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
    }

    // Clear selection when filters change (detect filter change by comparing with ref)
    const currentFilterString = JSON.stringify(filters);

    if (filtersRef.current !== currentFilterString) {
      setSelectedRowKeys([]);
      setSelectedUsers([]);
      filtersRef.current = currentFilterString;
      fetchUsers(1, state.limit, filters);
      return;
    }

    // Always fetch data with current state
    fetchUsers(state.page, state.limit, filters);
  }, [state.page, state.limit, filters, fetchUsers]);

  return (
    <>
      <div className="manage-features">
        {/* Header Section */}
        <div className="manage-features__header">
          <div className="header-left">
            <Typography.Title
              level={4}
              className="manage-features__title"
              style={{ marginBottom: 0 }}
            >
              Manage Features
            </Typography.Title>
          </div>

          <div className="header-right">
            <RangePicker
              size="large"
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              value={dateRange}
              onChange={handleDateRangeChange}
              disabledDate={disabledDate}
              placeholder={["Start date", "End date"]}
            />
          </div>
        </div>

        {/* Actions Section */}
        <div className="manage-features__controls">
          <div className="controls-top">
            <CustomSearchbar
              placeholder="Search by name, email, or phone"
              searchQuery={filters.search}
              setSearchQuery={(value) => onChangeFilters("search", value)}
              style={{
                marginBottom: 0,
                marginRight: 12,
                width: "100%",
                maxWidth: "100%",
              }}
            />

            <Space size={12}>
              <Tooltip title="Refresh">
                <Button size="large" onClick={reloadUsers}>
                  <ReloadOutlined />
                </Button>
              </Tooltip>

              <Button
                type="primary"
                size="large"
                icon={<SettingOutlined />}
                onClick={handleUpdateFeatures}
                style={{
                  backgroundColor: COLORS.PRIMARY,
                  borderColor: COLORS.PRIMARY,
                }}
              >
                Update features
                {selectedUsers.length > 0 && ` (${selectedUsers.length})`}
              </Button>

              {sensitiveAllowedActions?.includes("installCustomApp") &&
              sensitiveAllowedActions?.includes("uninstallCustomApp") ? (
                <Tooltip title="Install/Uninstall custom app from CSV only">
                  <Button
                    type="default"
                    size="large"
                    icon={<AppstoreOutlined />}
                    onClick={handleManageCustomApps}
                    style={{
                      borderColor: COLORS.PRIMARY,
                      color: COLORS.PRIMARY,
                    }}
                  >
                    Install/Uninstall custom app
                  </Button>
                </Tooltip>
              ) : null}
            </Space>
          </div>
        </div>

        {/* Users Table */}
        <div className="manage-features__table">
          <UsersTable
            data={state.list}
            loading={state.loading}
            pagination={{
              current: state.page,
              pageSize: state.limit,
              total: state.total,
            }}
            onTableChange={handleTableChange}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={handleSelectChange}
            onFilterChange={onChangeFilters}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      </div>

      {/* Update Features Modal */}
      {isModalOpen && (
        <ManageFeaturesModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            handleSelectChange([], []);
          }}
          onSuccess={reloadUsers}
          preloadedUsers={selectedUsers.length > 0 ? selectedUsers : null}
        />
      )}

      {/* Manage Custom Apps Modal */}
      {isCustomAppsModalOpen && (
        <ManageCustomAppsModal
          open={isCustomAppsModalOpen}
          onClose={() => setIsCustomAppsModalOpen(false)}
          onSuccess={() => {
            // You can add any success handling here if needed
            // For now, just show a success message as the API call handles the feedback
          }}
        />
      )}
    </>
  );
};

ManageFeatures.propTypes = {
  sensitiveAllowedActions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ManageFeatures;
